import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { analytics } from '../firebase';
import { logEvent } from '@firebase/analytics';

// Seed oils to detect (matching iOS app logic)
const SEED_OILS = [
  'canola oil', 'rapeseed oil',
  'soybean oil', 'soy oil',
  'sunflower oil',
  'safflower oil',
  'corn oil',
  'cottonseed oil',
  'grapeseed oil', 'grape seed oil',
  'rice bran oil',
  'vegetable oil',
  'palm oil', 'palm kernel oil',
  'peanut oil', 'groundnut oil',
];

// Variations and alternate names
const SEED_OIL_VARIATIONS = {
  'canola': ['canola oil', 'rapeseed oil', 'rapeseed'],
  'soybean': ['soybean oil', 'soy oil', 'soya oil'],
  'sunflower': ['sunflower oil', 'sunflower seed oil'],
  'safflower': ['safflower oil'],
  'corn': ['corn oil', 'maize oil'],
  'cottonseed': ['cottonseed oil', 'cotton seed oil'],
  'grapeseed': ['grapeseed oil', 'grape seed oil'],
  'rice bran': ['rice bran oil'],
  'vegetable': ['vegetable oil', 'vegetable fat'],
  'palm': ['palm oil', 'palm kernel oil', 'palm fat'],
  'peanut': ['peanut oil', 'groundnut oil'],
};

function detectSeedOils(ingredientsText) {
  if (!ingredientsText) return [];

  const normalizedText = ingredientsText.toLowerCase();
  const detected = [];

  Object.entries(SEED_OIL_VARIATIONS).forEach(([baseName, variations]) => {
    for (const variation of variations) {
      if (normalizedText.includes(variation)) {
        // Capitalize properly for display
        const displayName = baseName.charAt(0).toUpperCase() + baseName.slice(1) + ' Oil';
        if (!detected.find(d => d.name === displayName)) {
          detected.push({
            name: displayName,
            matchedText: variation,
          });
        }
        break;
      }
    }
  });

  return detected;
}

function ProductScan() {
  const { barcode } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detectedOils, setDetectedOils] = useState([]);

  useEffect(() => {
    async function fetchProduct() {
      setLoading(true);
      setError(null);

      try {
        // Fetch from OpenFoodFacts API
        const response = await fetch(
          `https://world.openfoodfacts.org/api/v0/product/${barcode}.json`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch product');
        }

        const data = await response.json();

        if (data.status === 0 || !data.product) {
          setError('Product not found');
          setProduct(null);
        } else {
          setProduct(data.product);

          // Detect seed oils
          const oils = detectSeedOils(data.product.ingredients_text);
          setDetectedOils(oils);

          // Log analytics
          logEvent(analytics, 'product_page_view', {
            barcode: barcode,
            product_name: data.product.product_name,
            has_seed_oils: oils.length > 0,
            seed_oil_count: oils.length,
          });
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Unable to load product information');
      } finally {
        setLoading(false);
      }
    }

    if (barcode) {
      fetchProduct();
    }
  }, [barcode]);

  const productName = product?.product_name || 'Unknown Product';
  const hasSeedOils = detectedOils.length > 0;
  const ingredientsText = product?.ingredients_text;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sage-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading product...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Product Not Found | FoodLens AI</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="text-6xl mb-6">🔍</div>
          <h1 className="font-display text-3xl text-charcoal mb-4">Product Not Found</h1>
          <p className="text-gray-500 mb-8">
            We couldn't find this product in our database. Try scanning it with the FoodLens AI app to contribute to our community database.
          </p>
          <a
            href="https://apps.apple.com/app/foodlens-ai/id6740037461"
            className="inline-block bg-sage-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-sage-700 transition-colors"
          >
            Get FoodLens AI
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{hasSeedOils ? `${detectedOils.length} Seed Oils in ${productName}` : `${productName} - Seed Oil Free`} | FoodLens AI</title>
        <meta
          name="description"
          content={hasSeedOils
            ? `Found ${detectedOils.length} seed oil${detectedOils.length > 1 ? 's' : ''} in ${productName}: ${detectedOils.map(o => o.name).join(', ')}. Scan your food with FoodLens AI.`
            : `${productName} is verified seed oil free! Scan your food with FoodLens AI to find clean products.`
          }
        />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={hasSeedOils ? `${detectedOils.length} Seed Oils Found in ${productName}` : `${productName} - Seed Oil Free!`} />
        <meta property="og:description" content={hasSeedOils ? `What's hiding in YOUR pantry? Scan free at foodlens-ai.com` : `Know before you buy. Scan free at foodlens-ai.com`} />
        <meta property="og:url" content={`https://foodlens-ai.com/product/${barcode}`} />
        <meta property="og:site_name" content="FoodLens AI" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={hasSeedOils ? `${detectedOils.length} Seed Oils in ${productName}` : `${productName} - Seed Oil Free`} />
        <meta name="twitter:description" content={hasSeedOils ? `What's hiding in YOUR pantry?` : `Know before you buy.`} />

        <link rel="canonical" href={`https://foodlens-ai.com/product/${barcode}`} />
      </Helmet>

      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm">
          <ol className="flex items-center space-x-2 text-gray-500">
            <li>
              <Link to="/" className="hover:text-sage-600 transition-colors">Home</Link>
            </li>
            <li><span className="mx-2">/</span></li>
            <li className="text-charcoal font-medium">Product Scan</li>
          </ol>
        </nav>

        {/* Product Name */}
        <div className="text-center mb-8">
          <h1 className="font-display text-2xl sm:text-3xl text-charcoal mb-2">
            {productName}
          </h1>
          {product?.brands && (
            <p className="text-gray-500">{product.brands}</p>
          )}
        </div>

        {/* Verdict Card */}
        <div className={`rounded-2xl p-6 sm:p-8 mb-8 ${hasSeedOils ? 'bg-red-50 border-2 border-red-200' : 'bg-green-50 border-2 border-green-200'}`}>
          <div className="text-center">
            {hasSeedOils ? (
              <>
                <div className="text-5xl mb-4">⚠️</div>
                <div className="text-red-600 font-bold text-lg mb-2">CONTAINS</div>
                <div className="text-red-700 font-display text-6xl sm:text-7xl font-bold mb-2">
                  {detectedOils.length}
                </div>
                <div className="text-red-600 font-bold text-xl">
                  SEED OIL{detectedOils.length > 1 ? 'S' : ''}
                </div>
              </>
            ) : (
              <>
                <div className="text-5xl mb-4">✓</div>
                <div className="text-green-600 font-bold text-sm tracking-widest mb-2">VERIFIED</div>
                <div className="text-green-700 font-display text-3xl sm:text-4xl font-bold">
                  SEED OIL FREE
                </div>
              </>
            )}
          </div>
        </div>

        {/* Detected Oils List */}
        {hasSeedOils && (
          <div className="bg-white rounded-xl p-6 mb-8 shadow-sm border border-gray-100">
            <h2 className="font-semibold text-charcoal mb-4">Detected Seed Oils:</h2>
            <ul className="space-y-2">
              {detectedOils.map((oil, index) => (
                <li key={index} className="flex items-center text-red-600">
                  <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                  {oil.name}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Ingredients (if available) */}
        {ingredientsText && (
          <div className="bg-white rounded-xl p-6 mb-8 shadow-sm border border-gray-100">
            <h2 className="font-semibold text-charcoal mb-3">Ingredients:</h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              {ingredientsText}
            </p>
          </div>
        )}

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-sage-50 to-sage-100 rounded-2xl p-6 sm:p-8 text-center">
          <h2 className="font-display text-xl sm:text-2xl text-charcoal mb-3">
            {hasSeedOils ? "What's hiding in YOUR pantry?" : "Scan your food before you buy"}
          </h2>
          <p className="text-gray-600 mb-6">
            FoodLens AI scans barcodes to instantly detect seed oils, artificial ingredients, and allergens.
          </p>
          <a
            href="https://apps.apple.com/app/foodlens-ai/id6740037461"
            className="inline-block bg-sage-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-sage-700 transition-colors mb-4"
            onClick={() => logEvent(analytics, 'app_store_click', { source: 'product_page', barcode })}
          >
            Get FoodLens AI - It's Free
          </a>
          <p className="text-sm text-gray-500">
            Available on iOS
          </p>
        </div>

        {/* Learn More Link */}
        <div className="mt-8 text-center">
          <Link to="/lenses/seed_oils" className="text-sage-600 hover:text-sage-700 font-medium">
            Learn why seed oils matter →
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ProductScan;
